import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import kebabCase from 'lodash/kebabCase';
import isNil from 'lodash/isNil';

import Popover from 'components/popover/Popover';

import './popover-scrollable.scss';

type ListItem = {
  color?: string,
  text: string,
  title?: string,
  url?: string
};

type Props = {
  list: Array<ListItem>,
  trigger?: JSX.Element,
  size?: 'small' | 'normal' | 'large',
  width?: number,
  height?: number,
  identifier?: string,
  disabled?: boolean,
  withEllipsis?: boolean,
  withFixedVerticalScroll?: boolean,
  classNameWrapper?: string,
  classNamePopover?: string,
  classNameTrigger?: string,
  classNameBody?: string
};

const PopoverScrollable: React.FunctionComponent<Props> = ({
  list = [],
  trigger,
  size = 'normal',
  width,
  height,
  identifier = 'popover-scrollable',
  disabled,
  withEllipsis,
  withFixedVerticalScroll,
  classNameWrapper,
  classNamePopover,
  classNameTrigger,
  classNameBody
}: Props) => {
  if (isEmpty(list)) {
    return null;
  }

  const triggerClasses = classnames('popover-scrollable__trigger', classNameTrigger, {
    'popover-scrollable__trigger--disabled': Boolean(disabled)
  });

  const bodyClasses = classnames('popover-scrollable__body', classNameBody, {
    [`popover-scrollable__body--size-${size}`]: Boolean(size),
    'popover-scrollable__body--always-vertical-scroll': Boolean(withFixedVerticalScroll)
  });

  const textClasses = classnames('popover-scrollable__text', {
    'text-overflow-ellipsis': Boolean(withEllipsis)
  });

  const styleOverrides = {
    ...(width ? { width } : null),
    ...(width ? { maxWidth: 'none' } : null),
    ...(height ? { height } : null),
    ...(height ? { maxHeight: 'none' } : null)
  };

  const getLink = (
    text: string,
    url?: string,
    color?: string
  ): JSX.Element => (
    <a
      href={url}
      className="popover-scrollable__link"
      target="_blank"
      rel="noopener noreferrer"
      style={{ color }}
    >
      {text}
    </a>
  );

  return (
    <div className="popover-scrollable">
      <Popover
        key={`popover-scrollable--${identifier}-${list.join('').length}`}
        triggerComponent={trigger}
        classNameWrapper={classNameWrapper}
        classNamePopover={classNamePopover}
        classNameTrigger={triggerClasses}
        showQuestionMark={isNil(trigger)}
      >
        <div
          className={bodyClasses}
          style={{ ...styleOverrides }}
        >
          {
            list.map((item, index) => (
              <React.Fragment key={`popover-scrollable--${kebabCase(item.text)}-${index}`}>
                <div
                  className={textClasses}
                  title={item.title}
                  style={{ color: item.color }}
                >
                  {
                    !isNil(item.url)
                      ? getLink(item.text, item.url, item.color)
                      : item.text
                  }
                </div>
              </React.Fragment>
            ))
          }
        </div>
      </Popover>
    </div>
  );
};

export default PopoverScrollable;
